import {
    Add,
    ArrowDownward,
    ArrowUpward,
    Check,
    Close,
    DataObject,
    DataObjectOutlined,
    Delete,
    DeviceHub,
    Category,
    Edit,
    Lan,
    Terminal as TerminalIcon,
    Description,
    HttpsOutlined,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { hasSchema } from "../../../utils/dashboardSchemaDraft";

/* -------------------------------------------------------------------------- */
/*  Helpers compartilhados                                                    */
/* -------------------------------------------------------------------------- */

function hasDoc(modelo) {
    return Boolean(modelo?.documentacao && String(modelo.documentacao).trim());
}

function safeParse(value, fallback) {
    if (value == null) return fallback;
    if (typeof value !== "string") return value;
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
}

/* -------------------------------------------------------------------------- */
/*  Card métrico genérico                                                     */
/* -------------------------------------------------------------------------- */

function MetricCard({ icon, label, value, color }) {
    const theme = useTheme();
    return (
        <Paper
            variant="outlined"
            sx={{
                flex: "1 1 140px",
                minWidth: 140,
                p: 1.5,
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                borderColor: theme.palette.divider,
            }}
        >
            <Box
                sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor:
                        theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.04)",
                    color: color || theme.palette.text.secondary,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: "block",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        fontSize: 11,
                    }}
                >
                    {label}
                </Typography>
                <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ lineHeight: 1.2 }}
                >
                    {value}
                </Typography>
            </Box>
        </Paper>
    );
}

/* -------------------------------------------------------------------------- */
/*  Sumário de Fabricante                                                     */
/* -------------------------------------------------------------------------- */

export function FabricanteSummary({
    fabricante,
    onCreateFuncao,
    onEditFuncao,
    onDeleteFuncao,
    onReorderFuncoes,
    onDirtyChange,
    resetSignal,
    notify,
}) {
    const theme = useTheme();
    const accent = theme.palette.colors?.blue?.light || theme.palette.primary.main;

    const [editMode, setEditMode] = useState(false);
    const [draft, setDraft] = useState([]); // [{key,id?,text,originalText,isNew,isDeleted}]
    const [editingKey, setEditingKey] = useState(null);
    const [editingValue, setEditingValue] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [addValue, setAddValue] = useState("");
    const [deleteTargetKey, setDeleteTargetKey] = useState(null);
    const [busy, setBusy] = useState(false);

    const funcoesValidas = useMemo(
        () => (fabricante?.Funcoes || []).filter((f) => f?.funcao != null),
        [fabricante]
    );

    const initialItems = useMemo(
        () =>
            funcoesValidas.map((f) => ({
                key: `e-${f.id}`,
                id: f.id,
                text: f.funcao,
                originalText: f.funcao,
                isNew: false,
                isDeleted: false,
            })),
        [funcoesValidas]
    );

    // sai do modo edição quando troca de fabricante
    useEffect(() => {
        setEditMode(false);
        setDraft([]);
        setEditingKey(null);
        setEditingValue("");
        setAddOpen(false);
        setAddValue("");
        setDeleteTargetKey(null);
    }, [fabricante?.id]);

    // descarta draft quando o pai sinaliza (ex.: usuário confirmou sair sem salvar)
    useEffect(() => {
        if (resetSignal == null) return;
        setEditMode(false);
        setDraft([]);
        setEditingKey(null);
        setEditingValue("");
        setAddOpen(false);
        setAddValue("");
        setDeleteTargetKey(null);
    }, [resetSignal]);

    const visibleItems = editMode
        ? draft.filter((d) => !d.isDeleted)
        : initialItems;

    const isDirty = useMemo(() => {
        if (!editMode) return false;
        const visible = draft.filter((d) => !d.isDeleted);
        if (visible.length !== initialItems.length) return true;
        for (let i = 0; i < visible.length; i++) {
            const d = visible[i];
            const o = initialItems[i];
            if (d.isNew) return true;
            if (!o || d.id !== o.id) return true;
            if (d.text !== o.text) return true;
        }
        return false;
    }, [editMode, draft, initialItems]);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    // ao desmontar (sumário sai de tela) garante limpeza do dirty
    useEffect(
        () => () => onDirtyChange?.(false),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const stats = useMemo(() => {
        const s = { funcoes: 0, modelos: 0, comSchema: 0, semSchema: 0 };
        for (const fun of fabricante?.Funcoes || []) {
            if (fun?.funcao == null) continue;
            s.funcoes += 1;
            for (const mod of fun.Modelos || []) {
                if (mod?.modelo == null) continue;
                s.modelos += 1;
                if (hasSchema(mod)) s.comSchema += 1;
                else s.semSchema += 1;
            }
        }
        return s;
    }, [fabricante]);

    const canEdit =
        typeof onCreateFuncao === "function" ||
        typeof onEditFuncao === "function" ||
        typeof onDeleteFuncao === "function" ||
        typeof onReorderFuncoes === "function";

    /* ----------- FLIP animation ----------- */
    const rowRefs = useRef(new Map());
    const prevPositions = useRef(new Map());
    const setRowRef = (key) => (el) => {
        if (el) rowRefs.current.set(key, el);
        else rowRefs.current.delete(key);
    };
    useLayoutEffect(() => {
        const next = new Map();
        rowRefs.current.forEach((el, key) => {
            next.set(key, el.getBoundingClientRect().top);
        });
        prevPositions.current.forEach((prevTop, key) => {
            const el = rowRefs.current.get(key);
            const newTop = next.get(key);
            if (!el || newTop == null) return;
            const delta = prevTop - newTop;
            if (Math.abs(delta) < 1) return;
            el.style.transition = "none";
            el.style.transform = `translateY(${delta}px)`;
            // eslint-disable-next-line no-unused-expressions
            el.getBoundingClientRect();
            requestAnimationFrame(() => {
                el.style.transition =
                    "transform 280ms cubic-bezier(0.2, 0.8, 0.2, 1)";
                el.style.transform = "translateY(0)";
            });
        });
        prevPositions.current = next;
    }, [visibleItems]);

    /* ----------- entrada/saída do modo edição ----------- */
    const enterEdit = () => {
        setDraft(initialItems.map((it) => ({ ...it })));
        setEditMode(true);
    };
    const cancelEdit = () => {
        setEditMode(false);
        setDraft([]);
        setEditingKey(null);
        setEditingValue("");
        setAddOpen(false);
        setAddValue("");
        setDeleteTargetKey(null);
    };

    /* ----------- ações inline (apenas no draft) ----------- */
    const startEditRow = (item) => {
        setEditingKey(item.key);
        setEditingValue(item.text || "");
    };
    const cancelEditRow = () => {
        setEditingKey(null);
        setEditingValue("");
    };
    const submitEditRow = () => {
        const text = editingValue.trim();
        if (!text || !editingKey) return;
        setDraft((prev) =>
            prev.map((d) => (d.key === editingKey ? { ...d, text } : d))
        );
        cancelEditRow();
    };
    const submitAdd = () => {
        const text = addValue.trim();
        if (!text) return;
        const key = `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setDraft((prev) => [
            ...prev,
            {
                key,
                id: null,
                text,
                originalText: "",
                isNew: true,
                isDeleted: false,
            },
        ]);
        setAddOpen(false);
        setAddValue("");
    };
    const submitDelete = () => {
        if (!deleteTargetKey) return;
        setDraft((prev) => {
            const target = prev.find((d) => d.key === deleteTargetKey);
            if (!target) return prev;
            if (target.isNew) return prev.filter((d) => d.key !== deleteTargetKey);
            return prev.map((d) =>
                d.key === deleteTargetKey ? { ...d, isDeleted: true } : d
            );
        });
        setDeleteTargetKey(null);
    };
    const moveRow = (idx, dir) => {
        const newIdx = idx + dir;
        if (newIdx < 0 || newIdx >= visibleItems.length) return;
        setDraft((prev) => {
            const visible = prev.filter((d) => !d.isDeleted);
            const deleted = prev.filter((d) => d.isDeleted);
            const reordered = [...visible];
            const [item] = reordered.splice(idx, 1);
            reordered.splice(newIdx, 0, item);
            return [...reordered, ...deleted];
        });
    };

    /* ----------- salvar (flush em ordem) ----------- */
    const handleSave = async () => {
        setBusy(true);
        try {
            // 1) Excluir
            const toDelete = draft.filter((d) => d.isDeleted && !d.isNew);
            for (const d of toDelete) {
                const ok = await onDeleteFuncao?.(d.id);
                if (!ok) return;
            }
            // 2) Renomear existentes
            const toUpdate = draft.filter(
                (d) =>
                    !d.isDeleted &&
                    !d.isNew &&
                    d.text.trim() &&
                    d.text.trim() !== d.originalText
            );
            for (const u of toUpdate) {
                const ok = await onEditFuncao?.(u.id, u.text.trim());
                if (!ok) return;
            }
            // 3) Criar novos (captura ids para o reorder)
            const visible = draft.filter((d) => !d.isDeleted);
            const newIds = new Map();
            for (const item of visible) {
                if (item.isNew && item.text.trim()) {
                    const created = await onCreateFuncao?.(item.text.trim());
                    if (!created || created.id == null) return;
                    newIds.set(item.key, created.id);
                }
            }
            // 4) Reordenar
            const orderItems = visible
                .map((it, i) => ({
                    id: it.isNew ? newIds.get(it.key) : it.id,
                    ordem: i,
                }))
                .filter((o) => o.id != null);
            if (orderItems.length > 0) {
                await onReorderFuncoes?.(orderItems);
            }
            // tudo ok → sai do modo edição
            cancelEdit();
            notify?.("Alterações salvas com sucesso", "success");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack spacing={2}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={1}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                        Fabricante
                    </Typography>
                    <Typography
                        variant="h5"
                        fontWeight={600}
                        sx={{ color: accent }}
                    >
                        {fabricante?.fabricante}
                    </Typography>
                </Box>
                {canEdit && !editMode && (
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={enterEdit}
                    >
                        Editar
                    </Button>
                )}
                {canEdit && editMode && (
                    <Stack direction="row" gap={1}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Close />}
                            disabled={busy}
                            onClick={cancelEdit}
                        >
                            Cancelar
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<Check />}
                            disabled={busy || !isDirty}
                            onClick={handleSave}
                        >
                            {busy ? "Salvando..." : "Salvar"}
                        </Button>
                    </Stack>
                )}
            </Stack>

            <Stack direction="row" gap={1.5} flexWrap="wrap">
                <MetricCard
                    icon={<Category />}
                    label="Funções"
                    value={stats.funcoes}
                    color={accent}
                />
                <MetricCard
                    icon={<DeviceHub />}
                    label="Modelos"
                    value={stats.modelos}
                    color={theme.palette.colors?.green?.light}
                />
                <MetricCard
                    icon={<DataObject />}
                    label="Com schema"
                    value={stats.comSchema}
                    color={theme.palette.colors?.green?.light}
                />
                <MetricCard
                    icon={<DataObjectOutlined />}
                    label="Sem schema"
                    value={stats.semSchema}
                    color={
                        stats.semSchema > 0
                            ? theme.palette.error.main
                            : theme.palette.text.disabled
                    }
                />
            </Stack>

            <Box>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                >
                    <Typography color="primary" fontWeight={500}>
                        Funções cadastradas
                    </Typography>
                    {editMode && (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => {
                                setAddValue("");
                                setAddOpen(true);
                            }}
                        >
                            Adicionar
                        </Button>
                    )}
                </Stack>
                <Divider sx={{ mb: 1 }} />
                {visibleItems.length === 0 ? (
                    <Chip
                        sx={{ bgcolor: theme.palette.background.arquive }}
                        label="Sem funções cadastradas"
                    />
                ) : (
                    <Stack gap={1}>
                        {visibleItems.map((item, idx) => {
                            const original = item.id
                                ? funcoesValidas.find((f) => f.id === item.id)
                                : null;
                            const totalMod = original
                                ? (original.Modelos || []).filter(
                                    (m) => m?.modelo != null
                                ).length
                                : 0;
                            const comSchema = original
                                ? (original.Modelos || []).filter(
                                    (m) => m?.modelo != null && hasSchema(m)
                                ).length
                                : 0;
                            const isEditingRow = editingKey === item.key;
                            return (
                                <Paper
                                    key={item.key}
                                    ref={setRowRef(item.key)}
                                    variant="outlined"
                                    sx={{
                                        p: 1.25,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        willChange: "transform",
                                        ...(item.isNew && {
                                            borderColor:
                                                theme.palette.colors?.green
                                                    ?.light,
                                            borderStyle: "dashed",
                                        }),
                                    }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 0,
                                            flex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        {isEditingRow ? (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                autoFocus
                                                value={editingValue}
                                                onChange={(e) =>
                                                    setEditingValue(
                                                        e.target.value
                                                    )
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter")
                                                        submitEditRow();
                                                    if (e.key === "Escape")
                                                        cancelEditRow();
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    fontWeight={500}
                                                    sx={{
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {item.text}
                                                    {item.isNew && (
                                                        <Typography
                                                            component="span"
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ ml: 1 }}
                                                        >
                                                            (novo)
                                                        </Typography>
                                                    )}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {item.isNew
                                                        ? "Será criado ao salvar"
                                                        : (
                                                            <>
                                                                {totalMod}{" "}
                                                                {totalMod === 1
                                                                    ? "modelo"
                                                                    : "modelos"}
                                                                {totalMod > 0 &&
                                                                    ` · ${comSchema} com schema`}
                                                            </>
                                                        )}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    {editMode ? (
                                        <Stack
                                            direction="row"
                                            gap={0.25}
                                            alignItems="center"
                                        >
                                            {isEditingRow ? (
                                                <>
                                                    <Tooltip title="Confirmar">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                disabled={busy}
                                                                onClick={
                                                                    submitEditRow
                                                                }
                                                            >
                                                                <Check fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Cancelar">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={busy}
                                                                onClick={
                                                                    cancelEditRow
                                                                }
                                                            >
                                                                <Close fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <>
                                                    <Tooltip title="Mover para cima">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={
                                                                    busy ||
                                                                    idx === 0
                                                                }
                                                                onClick={() =>
                                                                    moveRow(
                                                                        idx,
                                                                        -1
                                                                    )
                                                                }
                                                            >
                                                                <ArrowUpward fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Mover para baixo">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={
                                                                    busy ||
                                                                    idx ===
                                                                    visibleItems.length -
                                                                    1
                                                                }
                                                                onClick={() =>
                                                                    moveRow(
                                                                        idx,
                                                                        1
                                                                    )
                                                                }
                                                            >
                                                                <ArrowDownward fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Editar">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                disabled={busy}
                                                                onClick={() =>
                                                                    startEditRow(
                                                                        item
                                                                    )
                                                                }
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Excluir">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                disabled={busy}
                                                                onClick={() =>
                                                                    setDeleteTargetKey(
                                                                        item.key
                                                                    )
                                                                }
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Stack>
                                    ) : (
                                        <Chip
                                            size="small"
                                            label={`${totalMod}`}
                                            sx={{
                                                bgcolor:
                                                    theme.palette.colors
                                                        ?.purple?.light,
                                                color: "white",
                                                fontWeight: 600,
                                                minWidth: 36,
                                            }}
                                        />
                                    )}
                                </Paper>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            {/* Modal: adicionar função */}
            <Dialog
                open={addOpen}
                onClose={() => (busy ? null : setAddOpen(false))}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Adicionar Função</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Nome da função"
                        value={addValue}
                        onChange={(e) => setAddValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") submitAdd();
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setAddOpen(false)}
                        disabled={busy}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitAdd}
                        disabled={busy || !addValue.trim()}
                    >
                        Adicionar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal: confirmar exclusão */}
            <Dialog
                open={Boolean(deleteTargetKey)}
                onClose={() => (busy ? null : setDeleteTargetKey(null))}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Excluir Função</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja excluir a função
                        {" "}
                        <strong>
                            {
                                draft.find((d) => d.key === deleteTargetKey)
                                    ?.text
                            }
                        </strong>
                        ? A exclusão será efetivada ao clicar em
                        {" "}<strong>Salvar</strong>. Todos os modelos
                        associados a esta função também serão excluídos.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteTargetKey(null)}
                        disabled={busy}
                    >
                        Cancelar
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={submitDelete}
                        disabled={busy}
                    >
                        Marcar para excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

/* -------------------------------------------------------------------------- */
/*  Sumário de Função                                                         */
/* -------------------------------------------------------------------------- */

export function FuncaoSummary({
    funcao,
    fabricanteNome,
    onCreateModelo,
    onEditModelo,
    onDeleteModelo,
    onReorderModelos,
    onDirtyChange,
    resetSignal,
    notify,
}) {
    const theme = useTheme();
    const accent =
        theme.palette.colors?.purple?.light || theme.palette.primary.main;

    const [editMode, setEditMode] = useState(false);
    const [draft, setDraft] = useState([]);
    const [editingKey, setEditingKey] = useState(null);
    const [editingValue, setEditingValue] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [addValue, setAddValue] = useState("");
    const [deleteTargetKey, setDeleteTargetKey] = useState(null);
    const [busy, setBusy] = useState(false);

    const modelosValidos = useMemo(
        () => (funcao?.Modelos || []).filter((m) => m?.modelo != null),
        [funcao]
    );

    const initialItems = useMemo(
        () =>
            modelosValidos.map((m) => ({
                key: `e-${m.id}`,
                id: m.id,
                text: m.modelo,
                originalText: m.modelo,
                isNew: false,
                isDeleted: false,
            })),
        [modelosValidos]
    );

    useEffect(() => {
        setEditMode(false);
        setDraft([]);
        setEditingKey(null);
        setEditingValue("");
        setAddOpen(false);
        setAddValue("");
        setDeleteTargetKey(null);
    }, [funcao?.id]);

    useEffect(() => {
        if (resetSignal == null) return;
        setEditMode(false);
        setDraft([]);
        setEditingKey(null);
        setEditingValue("");
        setAddOpen(false);
        setAddValue("");
        setDeleteTargetKey(null);
    }, [resetSignal]);

    const visibleItems = editMode
        ? draft.filter((d) => !d.isDeleted)
        : initialItems;

    const isDirty = useMemo(() => {
        if (!editMode) return false;
        const visible = draft.filter((d) => !d.isDeleted);
        if (visible.length !== initialItems.length) return true;
        for (let i = 0; i < visible.length; i++) {
            const d = visible[i];
            const o = initialItems[i];
            if (d.isNew) return true;
            if (!o || d.id !== o.id) return true;
            if (d.text !== o.text) return true;
        }
        return false;
    }, [editMode, draft, initialItems]);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    useEffect(
        () => () => onDirtyChange?.(false),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const stats = useMemo(() => {
        const s = { modelos: 0, comSchema: 0, semSchema: 0, comDoc: 0 };
        for (const mod of funcao?.Modelos || []) {
            if (mod?.modelo == null) continue;
            s.modelos += 1;
            if (hasSchema(mod)) s.comSchema += 1;
            else s.semSchema += 1;
            if (hasDoc(mod)) s.comDoc += 1;
        }
        return s;
    }, [funcao]);

    const canEdit =
        typeof onCreateModelo === "function" ||
        typeof onEditModelo === "function" ||
        typeof onDeleteModelo === "function" ||
        typeof onReorderModelos === "function";

    /* ----------- FLIP animation ----------- */
    const rowRefs = useRef(new Map());
    const prevPositions = useRef(new Map());
    const setRowRef = (key) => (el) => {
        if (el) rowRefs.current.set(key, el);
        else rowRefs.current.delete(key);
    };
    useLayoutEffect(() => {
        const next = new Map();
        rowRefs.current.forEach((el, key) => {
            next.set(key, el.getBoundingClientRect().top);
        });
        prevPositions.current.forEach((prevTop, key) => {
            const el = rowRefs.current.get(key);
            const newTop = next.get(key);
            if (!el || newTop == null) return;
            const delta = prevTop - newTop;
            if (Math.abs(delta) < 1) return;
            el.style.transition = "none";
            el.style.transform = `translateY(${delta}px)`;
            // eslint-disable-next-line no-unused-expressions
            el.getBoundingClientRect();
            requestAnimationFrame(() => {
                el.style.transition =
                    "transform 280ms cubic-bezier(0.2, 0.8, 0.2, 1)";
                el.style.transform = "translateY(0)";
            });
        });
        prevPositions.current = next;
    }, [visibleItems]);

    const enterEdit = () => {
        setDraft(initialItems.map((it) => ({ ...it })));
        setEditMode(true);
    };
    const cancelEdit = () => {
        setEditMode(false);
        setDraft([]);
        setEditingKey(null);
        setEditingValue("");
        setAddOpen(false);
        setAddValue("");
        setDeleteTargetKey(null);
    };

    const startEditRow = (item) => {
        setEditingKey(item.key);
        setEditingValue(item.text || "");
    };
    const cancelEditRow = () => {
        setEditingKey(null);
        setEditingValue("");
    };
    const submitEditRow = () => {
        const text = editingValue.trim();
        if (!text || !editingKey) return;
        setDraft((prev) =>
            prev.map((d) => (d.key === editingKey ? { ...d, text } : d))
        );
        cancelEditRow();
    };
    const submitAdd = () => {
        const text = addValue.trim();
        if (!text) return;
        const key = `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setDraft((prev) => [
            ...prev,
            {
                key,
                id: null,
                text,
                originalText: "",
                isNew: true,
                isDeleted: false,
            },
        ]);
        setAddOpen(false);
        setAddValue("");
    };
    const submitDelete = () => {
        if (!deleteTargetKey) return;
        setDraft((prev) => {
            const target = prev.find((d) => d.key === deleteTargetKey);
            if (!target) return prev;
            if (target.isNew) return prev.filter((d) => d.key !== deleteTargetKey);
            return prev.map((d) =>
                d.key === deleteTargetKey ? { ...d, isDeleted: true } : d
            );
        });
        setDeleteTargetKey(null);
    };
    const moveRow = (idx, dir) => {
        const newIdx = idx + dir;
        if (newIdx < 0 || newIdx >= visibleItems.length) return;
        setDraft((prev) => {
            const visible = prev.filter((d) => !d.isDeleted);
            const deleted = prev.filter((d) => d.isDeleted);
            const reordered = [...visible];
            const [item] = reordered.splice(idx, 1);
            reordered.splice(newIdx, 0, item);
            return [...reordered, ...deleted];
        });
    };

    const handleSave = async () => {
        setBusy(true);
        try {
            const toDelete = draft.filter((d) => d.isDeleted && !d.isNew);
            for (const d of toDelete) {
                const ok = await onDeleteModelo?.(d.id);
                if (!ok) return;
            }
            const toUpdate = draft.filter(
                (d) =>
                    !d.isDeleted &&
                    !d.isNew &&
                    d.text.trim() &&
                    d.text.trim() !== d.originalText
            );
            for (const u of toUpdate) {
                const ok = await onEditModelo?.(u.id, u.text.trim());
                if (!ok) return;
            }
            const visible = draft.filter((d) => !d.isDeleted);
            const newIds = new Map();
            for (const item of visible) {
                if (item.isNew && item.text.trim()) {
                    const created = await onCreateModelo?.(item.text.trim());
                    if (!created || created.id == null) return;
                    newIds.set(item.key, created.id);
                }
            }
            const orderItems = visible
                .map((it, i) => ({
                    id: it.isNew ? newIds.get(it.key) : it.id,
                    ordem: i,
                }))
                .filter((o) => o.id != null);
            if (orderItems.length > 0) {
                await onReorderModelos?.(orderItems);
            }
            cancelEdit();
            notify?.("Alterações salvas com sucesso", "success");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack spacing={2}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={1}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                        {fabricanteNome
                            ? `Fabricante: ${fabricanteNome} › Função`
                            : "Função"}
                    </Typography>
                    <Typography
                        variant="h5"
                        fontWeight={600}
                        sx={{ color: accent }}
                    >
                        {funcao?.funcao}
                    </Typography>
                </Box>
                {canEdit && !editMode && (
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={enterEdit}
                    >
                        Editar
                    </Button>
                )}
                {canEdit && editMode && (
                    <Stack direction="row" gap={1}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Close />}
                            disabled={busy}
                            onClick={cancelEdit}
                        >
                            Cancelar
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<Check />}
                            disabled={busy || !isDirty}
                            onClick={handleSave}
                        >
                            {busy ? "Salvando..." : "Salvar"}
                        </Button>
                    </Stack>
                )}
            </Stack>

            <Stack direction="row" gap={1.5} flexWrap="wrap">
                <MetricCard
                    icon={<DeviceHub />}
                    label="Modelos"
                    value={stats.modelos}
                    color={accent}
                />
                <MetricCard
                    icon={<DataObject />}
                    label="Com schema"
                    value={stats.comSchema}
                    color={theme.palette.colors?.green?.light}
                />
                <MetricCard
                    icon={<DataObjectOutlined />}
                    label="Sem schema"
                    value={stats.semSchema}
                    color={
                        stats.semSchema > 0
                            ? theme.palette.error.main
                            : theme.palette.text.disabled
                    }
                />
                <MetricCard
                    icon={<Description />}
                    label="Com documentação"
                    value={stats.comDoc}
                    color={theme.palette.colors?.blue?.light}
                />
            </Stack>

            <Box>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                >
                    <Typography color="primary" fontWeight={500}>
                        Modelos cadastrados
                    </Typography>
                    {editMode && (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => {
                                setAddValue("");
                                setAddOpen(true);
                            }}
                        >
                            Adicionar
                        </Button>
                    )}
                </Stack>
                <Divider sx={{ mb: 1 }} />
                {visibleItems.length === 0 ? (
                    <Chip
                        sx={{ bgcolor: theme.palette.background.arquive }}
                        label="Sem modelos cadastrados"
                    />
                ) : (
                    <Stack gap={1}>
                        {visibleItems.map((item, idx) => {
                            const original = item.id
                                ? modelosValidos.find((m) => m.id === item.id)
                                : null;
                            const schemaOk = original
                                ? hasSchema(original)
                                : false;
                            const docOk = original ? hasDoc(original) : false;
                            const isEditingRow = editingKey === item.key;
                            return (
                                <Paper
                                    key={item.key}
                                    ref={setRowRef(item.key)}
                                    variant="outlined"
                                    sx={{
                                        p: 1.25,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        willChange: "transform",
                                        ...(item.isNew && {
                                            borderColor:
                                                theme.palette.colors?.green
                                                    ?.light,
                                            borderStyle: "dashed",
                                        }),
                                    }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 0,
                                            flex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        {isEditingRow ? (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                autoFocus
                                                value={editingValue}
                                                onChange={(e) =>
                                                    setEditingValue(
                                                        e.target.value
                                                    )
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter")
                                                        submitEditRow();
                                                    if (e.key === "Escape")
                                                        cancelEditRow();
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    fontWeight={500}
                                                    sx={{
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {item.text}
                                                    {item.isNew && (
                                                        <Typography
                                                            component="span"
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ ml: 1 }}
                                                        >
                                                            (novo)
                                                        </Typography>
                                                    )}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {item.isNew
                                                        ? "Será criado ao salvar"
                                                        : (
                                                            <>
                                                                {schemaOk
                                                                    ? "Com schema"
                                                                    : "Sem schema"}
                                                                {docOk
                                                                    ? " · com documentação"
                                                                    : ""}
                                                            </>
                                                        )}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    {editMode ? (
                                        <Stack
                                            direction="row"
                                            gap={0.25}
                                            alignItems="center"
                                        >
                                            {isEditingRow ? (
                                                <>
                                                    <Tooltip title="Confirmar">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                disabled={busy}
                                                                onClick={
                                                                    submitEditRow
                                                                }
                                                            >
                                                                <Check fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Cancelar">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={busy}
                                                                onClick={
                                                                    cancelEditRow
                                                                }
                                                            >
                                                                <Close fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <>
                                                    <Tooltip title="Mover para cima">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={
                                                                    busy ||
                                                                    idx === 0
                                                                }
                                                                onClick={() =>
                                                                    moveRow(
                                                                        idx,
                                                                        -1
                                                                    )
                                                                }
                                                            >
                                                                <ArrowUpward fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Mover para baixo">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={
                                                                    busy ||
                                                                    idx ===
                                                                    visibleItems.length -
                                                                    1
                                                                }
                                                                onClick={() =>
                                                                    moveRow(
                                                                        idx,
                                                                        1
                                                                    )
                                                                }
                                                            >
                                                                <ArrowDownward fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Editar">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                disabled={busy}
                                                                onClick={() =>
                                                                    startEditRow(
                                                                        item
                                                                    )
                                                                }
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Excluir">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                disabled={busy}
                                                                onClick={() =>
                                                                    setDeleteTargetKey(
                                                                        item.key
                                                                    )
                                                                }
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Stack>
                                    ) : (
                                        <Stack direction="row" gap={0.5}>
                                            <Chip
                                                size="small"
                                                label={
                                                    schemaOk ? "schema" : "—"
                                                }
                                                sx={{
                                                    bgcolor: schemaOk
                                                        ? theme.palette.colors
                                                            ?.green?.light
                                                        : theme.palette
                                                            .background
                                                            .arquive,
                                                    color: schemaOk
                                                        ? "white"
                                                        : undefined,
                                                    fontWeight: 600,
                                                }}
                                            />
                                            {docOk && (
                                                <Chip
                                                    size="small"
                                                    label="doc"
                                                    sx={{
                                                        bgcolor:
                                                            theme.palette
                                                                .colors?.blue
                                                                ?.light,
                                                        color: "white",
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                    )}
                                </Paper>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            {/* Modal: adicionar modelo */}
            <Dialog
                open={addOpen}
                onClose={() => (busy ? null : setAddOpen(false))}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Adicionar Modelo</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Nome do modelo"
                        value={addValue}
                        onChange={(e) => setAddValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") submitAdd();
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setAddOpen(false)}
                        disabled={busy}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitAdd}
                        disabled={busy || !addValue.trim()}
                    >
                        Adicionar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal: confirmar exclusão */}
            <Dialog
                open={Boolean(deleteTargetKey)}
                onClose={() => (busy ? null : setDeleteTargetKey(null))}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Excluir Modelo</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja excluir o modelo
                        {" "}
                        <strong>
                            {
                                draft.find((d) => d.key === deleteTargetKey)
                                    ?.text
                            }
                        </strong>
                        ? A exclusão será efetivada ao clicar em
                        {" "}<strong>Salvar</strong>.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteTargetKey(null)}
                        disabled={busy}
                    >
                        Cancelar
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={submitDelete}
                        disabled={busy}
                    >
                        Marcar para excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack >
    );
}

/* -------------------------------------------------------------------------- */
/*  Sumário de Modelo                                                         */
/* -------------------------------------------------------------------------- */

export function ModeloSummary({
    modelo,
    fabricanteNome,
    funcaoNome,
    onEditar,
}) {
    const theme = useTheme();
    const accent =
        theme.palette.colors?.green?.light || theme.palette.primary.main;

    const predef = modelo?.Predefinicoes || {};
    const groups = safeParse(predef.groups, []) || [];
    const templatesArr = safeParse(predef.templates, []) || [];
    const macrosArr = safeParse(predef.macros, []) || [];
    const config = predef.config || {};
    const sshOn = !!config.SSH;
    const telOn = !!config.Telnet;

    const schemaOk = hasSchema(modelo);
    const docOk = hasDoc(modelo);

    return (
        <Stack spacing={2}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                        {fabricanteNome}
                        {funcaoNome ? ` › ${funcaoNome}` : ""}
                    </Typography>
                    <Typography
                        variant="h5"
                        fontWeight={600}
                        sx={{ color: accent }}
                    >
                        {modelo?.modelo}
                    </Typography>
                </Box>
                {onEditar && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<HttpsOutlined />}
                        onClick={onEditar}
                    >
                        Editar
                    </Button>
                )}
            </Box>

            <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip
                    size="small"
                    icon={
                        schemaOk ? (
                            <DataObject sx={{ fontSize: 16 }} />
                        ) : (
                            <DataObjectOutlined sx={{ fontSize: 16 }} />
                        )
                    }
                    label={schemaOk ? "Com schema" : "Sem schema"}
                    sx={{
                        bgcolor: schemaOk
                            ? theme.palette.colors?.green?.light
                            : theme.palette.background.arquive,
                        color: schemaOk ? "white" : "inherit",
                    }}
                />
                <Chip
                    size="small"
                    icon={<Description sx={{ fontSize: 16 }} />}
                    label={docOk ? "Com documentação" : "Sem documentação"}
                    sx={{
                        bgcolor: docOk
                            ? theme.palette.colors?.purple?.light
                            : theme.palette.background.arquive,
                        color: docOk ? "white" : "inherit",
                    }}
                />
                {sshOn && (
                    <Chip
                        size="small"
                        icon={<TerminalIcon sx={{ fontSize: 16 }} />}
                        label="SSH"
                        sx={{
                            bgcolor: theme.palette.chip.waitingReturn,
                            color: "white",
                        }}
                    />
                )}
                {telOn && (
                    <Chip
                        size="small"
                        icon={<Lan sx={{ fontSize: 16 }} />}
                        label="Telnet"
                        sx={{
                            bgcolor: theme.palette.chip.waitingReturn,
                            color: "white",
                        }}
                    />
                )}
            </Stack>

            <Stack direction="row" gap={1.5} flexWrap="wrap">
                <MetricCard
                    icon={<Lan />}
                    label="Grupos"
                    value={groups.length}
                    color={theme.palette.colors?.blue?.light}
                />
                <MetricCard
                    icon={<DeviceHub />}
                    label="Templates"
                    value={templatesArr.length}
                    color={theme.palette.colors?.purple?.light}
                />
                <MetricCard
                    icon={<Category />}
                    label="Macros"
                    value={
                        macrosArr.filter(
                            (m) => m && (m.macro || m.valor || m.tag)
                        ).length
                    }
                    color={theme.palette.colors?.green?.light}
                />
            </Stack>

            <Box>
                <Typography color="primary" fontWeight={500} sx={{ mb: 1 }}>
                    Grupos de hosts
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {groups.length === 0 ? (
                    <Chip
                        size="small"
                        sx={{ bgcolor: theme.palette.background.arquive }}
                        label="Sem grupos"
                    />
                ) : (
                    <Box
                        component="ul"
                        sx={{
                            listStyle: "none",
                            p: 0,
                            m: 0,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                        }}
                    >
                        {groups.map((g, i) => (
                            <li key={g.groupid || i}>
                                <Chip
                                    size="small"
                                    label={g.name}
                                    sx={{
                                        bgcolor:
                                            theme.palette.chip.waitingReturn,
                                        color: "white",
                                    }}
                                />
                            </li>
                        ))}
                    </Box>
                )}
            </Box>

            <Box>
                <Typography color="primary" fontWeight={500} sx={{ mb: 1 }}>
                    Templates
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {templatesArr.length === 0 ? (
                    <Chip
                        size="small"
                        sx={{ bgcolor: theme.palette.background.arquive }}
                        label="Sem templates"
                    />
                ) : (
                    <Box
                        component="ul"
                        sx={{
                            listStyle: "none",
                            p: 0,
                            m: 0,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                        }}
                    >
                        {templatesArr.map((t, i) => (
                            <li key={t.templateid || i}>
                                <Chip
                                    size="small"
                                    label={t.name}
                                    sx={{
                                        bgcolor:
                                            theme.palette.chip.waitingReturn,
                                        color: "white",
                                    }}
                                />
                            </li>
                        ))}
                    </Box>
                )}
            </Box>
        </Stack>
    );
}
